import { createSelector } from 'reselect';
import { includes, transform } from 'lodash';
import core, { App, View } from '@gin-betting/core';

import {
    filterScoresBySportAndMatchPart,
    getCurrentMatchPart,
    getRouteBySportType,
} from 'utils';
import { MatchPartInfo, State } from 'types';
import {
    DEFAULT_STREAM_URL,
    VIEWABLE_SPORT_LIST,
    MetaName,
} from 'app-constants';
import { CONFIG_BY_SPORTS } from 'scoreMetaConfig';

const {
    entities: {
        selectors: {
            makeSelectMatchViewScores,
            selectMatch,
            makeMatchSelector,
            selectMobileMatchStream,
            selectMatchMeta,
            selectSportBySportEvent,
            selectSportByTournament,
        },
    },
    utils: { getSportTypeBySport },
} = core;

const scoreOrderBySport: {
    [key: string]: Partial<{ [key in App.ScoreTypes]: number }>;
} = transform(CONFIG_BY_SPORTS, (acc, { scoresOrder }, sportId) => {
    if (scoresOrder) {
        acc[sportId] = scoresOrder;
    }
});

export const selectMatchViewScores = makeSelectMatchViewScores(
    scoreOrderBySport
);

export const selectFilteredScores = createSelector(
    selectMatch,
    selectMatchViewScores,
    selectMatchMeta,
    (match, scores, meta) => {
        let currentMatchPart: MatchPartInfo | null = null;

        if (match) {
            currentMatchPart = getCurrentMatchPart(meta, match);

            return {
                scores: filterScoresBySportAndMatchPart(
                    match.sportId,
                    currentMatchPart,
                    scores
                ),
                currentMatchPart,
            };
        }

        return { scores, currentMatchPart };
    }
);

export const makeSelectMetaValueByName = <T extends MetaName>(metaName: T) =>
    createSelector(
        selectMatchMeta,
        meta => meta.find(m => m.name === metaName)?.value ?? null
    );

export const selectBetradarLMTCoverageLevel = makeSelectMetaValueByName(
    MetaName.BetradarLMTCoverageLevel
);
export const selectBetradarId = createSelector(
    selectBetradarLMTCoverageLevel,
    makeSelectMetaValueByName(MetaName.BetradarId),
    (lmt, betradarId) => (lmt && lmt !== '1.00' ? betradarId : null)
);
export const selectMatchTime = makeSelectMetaValueByName(MetaName.Time);
export const selectTimerRunning = makeSelectMetaValueByName(
    MetaName.TimerRunning
);

export const selectIsSportEventViewable = createSelector(
    selectMobileMatchStream,
    selectBetradarId,
    makeMatchSelector({
        propsToPick: ['sportId'],
    }),
    (streamUrl, betradarId, match) => {
        const sportId = match?.sportId;

        const hasValidStream =
            !!streamUrl && streamUrl !== '' && streamUrl !== DEFAULT_STREAM_URL;

        const hasValidBetradarId =
            !!betradarId && !!sportId && includes(VIEWABLE_SPORT_LIST, sportId);

        return {
            hasValidStream,
            hasValidBetradarId,
        };
    }
);

type SelectSport = (state: State, props: object) => View.Sport | undefined;

export const makeSelectRouteBySportEvent = (selectSport: SelectSport) =>
    createSelector(selectSport, sport =>
        getRouteBySportType(getSportTypeBySport(sport))
    );

export const selectRouteBySportEvent = makeSelectRouteBySportEvent(
    selectSportBySportEvent
);

export const selectRouteByTournament = makeSelectRouteBySportEvent(
    selectSportByTournament
);
